// ═══════════════════════════════════════════════════════════════════════════════
// Bottom navigation (flex footer in MainAppShell) — employer vs helper targets
// ═══════════════════════════════════════════════════════════════════════════════

import { NavLink, useLocation } from 'react-router-dom';
import { USER_ROLE } from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { BOTTOM_NAV_BROWSER_CLASS, BOTTOM_NAV_PWA_CLASS } from '../../lib/layoutChrome';

type NavKey = 'home' | 'tasks' | 'settlements' | 'family' | 'stock';

const homePath = (role: string | undefined): string =>
  role === USER_ROLE.EMPLOYER ? '/dashboard' : '/home';

const tasksPath = (role: string | undefined): string =>
  role === USER_ROLE.EMPLOYER ? '/tasks' : '/today';

const isActivePath = (pathname: string, key: NavKey, role: string | undefined): boolean => {
  const h = homePath(role);
  if (key === 'home') {
    return pathname === h || pathname === `${h}/`;
  }
  if (key === 'tasks') {
    if (role === USER_ROLE.EMPLOYER) {
      return (
        pathname.startsWith('/tasks') ||
        pathname.startsWith('/task-instances')
      );
    }
    return (
      pathname.startsWith('/today') ||
      pathname.startsWith('/tasks/complete') ||
      pathname.startsWith('/tasks/resubmit') ||
      pathname.startsWith('/tasks/inventory') ||
      pathname.startsWith('/task-instances')
    );
  }
  if (key === 'settlements') {
    return (
      pathname.startsWith('/settlements') ||
      pathname.startsWith('/receipts') ||
      pathname.startsWith('/budgets')
    );
  }
  if (key === 'family') {
    return pathname.startsWith('/family-members');
  }
  if (key === 'stock') {
    return pathname.startsWith('/stock/latest-home-stock');
  }
  return false;
};

const EMPLOYER_HOME_ICON_PATH =
  'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6';

const HELPER_HOME_ICON_PATH = 'M3 12l2-2 7-7 7 7 2 2M5 10v11h14V10';

const NavIcon = ({ name, role }: { name: NavKey; role?: string }) => {
  const cls = 'h-[24px] w-[24px]';
  switch (name) {
    case 'home':
      if (role === USER_ROLE.HELPER) {
        return (
          <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={HELPER_HOME_ICON_PATH}
            />
            <rect x="8" y="12" width="2.5" height="2.5" strokeWidth={1} />
            <rect x="13.5" y="12" width="2.5" height="2.5" strokeWidth={1} />
          </svg>
        );
      }
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={EMPLOYER_HOME_ICON_PATH}
          />
        </svg>
      );
    case 'tasks':
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
          />
        </svg>
      );
    case 'settlements':
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
          />
        </svg>
      );
    case 'family':
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      );
    case 'stock':
      return (
        <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20 7l-8 4-8-4m16 0l-8-4-8 4m16 0v10l-8 4m8-14l-8 4m0 10l-8-4V7m8 14V11"
          />
        </svg>
      );
    default:
      return null;
  }
};

type BottomNavProps = {
  /** When true (PWA), nav is in flex flow; when false (browser), nav is fixed to viewport bottom. */
  usePwaLayout?: boolean;
};

export const BottomNav = ({ usePwaLayout = true }: BottomNavProps) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;
  const role = user?.role;

  const items: Array<{ key: NavKey; to: string; label: string }> = [
    { key: 'home', to: homePath(role), label: t('nav.home') },
    { key: 'tasks', to: tasksPath(role), label: t('nav.tasks') },
    {
      key: 'settlements',
      to: '/settlements',
      label:
        role === USER_ROLE.HELPER ? t('nav.expensesSettlementsHelper') : t('nav.expensesSettlements'),
    },
    { key: 'family', to: '/family-members', label: t('nav.family') },
    { key: 'stock', to: '/stock/latest-home-stock', label: t('nav.stock') },
  ];

  const navClassName = usePwaLayout ? BOTTOM_NAV_PWA_CLASS : BOTTOM_NAV_BROWSER_CLASS;

  return (
    <nav
      className={navClassName}
      style={{ fontSize: '11px', lineHeight: '14px' }}
      aria-label={t('nav.mainAria')}
    >
      <div className="mx-auto flex h-[64px] max-w-lg items-stretch justify-around">
        {items.map((item) => {
          const routeActive = isActivePath(pathname, item.key, role);
          return (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.key === 'home'}
              aria-current={routeActive ? 'page' : undefined}
              className="flex min-w-0 flex-1 flex-col items-center justify-center gap-[4px] px-[4px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              style={{ minHeight: '44px', fontSize: '11px', lineHeight: '14px' }}
            >
              {({ isActive }) => {
                const on = routeActive || isActive;
                return (
                  <span
                    className={
                      on
                        ? 'flex w-full max-w-[88px] flex-col items-center gap-[4px] rounded-2xl bg-primary-100 px-[12px] py-[8px] text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                        : 'flex w-full max-w-[88px] flex-col items-center gap-[4px] rounded-2xl px-[12px] py-[8px] text-gray-500 dark:text-gray-400'
                    }
                  >
                    <span>
                      <NavIcon name={item.key} role={role} />
                    </span>
                    <span style={{ fontSize: '11px', lineHeight: '14px' }}>{item.label}</span>
                  </span>
                );
              }}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};
